<template>
<div class="theme-layout">
    <div v-show="false"><!-- Hide tippy initially -->
        <SketchPicker
            :value="activeTheme.value"
            @input="updateTheme(activeTheme, $event)"
            ref="picker"
            class="color-picker" />
    </div>
    <!-- Need some slightly complicated mouse event handlers, since the
        color picker is not a DOM child of the tippyRefs.-->
    <div
        @mouseleave="closePickers"
        class="field is-grouped is-grouped-multiline">
        <div
            v-for="theme in localTheme"
            class="control"
            :key="theme.key">
            <label class="label">{{ theme.displayName }}</label>
            <div
                @mouseenter="setActiveTheme(theme)"
                class="field has-addons color-field">
                <div class="control" ref="tippyRef">
                    <a :style="{ background: theme.value }" class="button">
                        <FaIcon icon="ellipsis-v"/>
                    </a>
                </div>
                <div class="control">
                    <input
                        v-model="theme.value"
                        class="input"
                        required/>
                </div>
            </div>
            <p class="help">{{ theme.desc }}</p>
        </div>
    </div>
    <label class="label">Theme</label>
    <div class="theme-options">
        <Radio v-model="mode" value="light" name="mode">Light</Radio>
        <Radio v-model="mode" value="dark" name="mode">Dark</Radio>
        <Radio v-model="mode" value="user" name="mode"
            data-tippy-content="Set the theme according to the user's settings."
            ref="userPref">User preference</Radio>
    </div>
</div>
</template>

<script>
import SketchPicker from 'vue-color/src/components/Sketch'

import * as _ from '@/lodash'
import tippy, { createSingleton } from '@/tippy'

export default {
    name: 'Theme',
    props: {
        theme: Object,
    },
    data: () => ({
        localTheme: [],
        mode: 'user',
    }),
    computed: {
        activeTheme: vm => {
            return vm.localTheme.find(t => t.isPickerOpen) || { value: '#fff' }
        },
    },
    watch: {
        theme: {
            handler () {
                // Convert to array of objects for easier v-model usage
                this.localTheme = Object
                    .entries(this.theme)
                    .filter(([key, val]) => {
                        // TODO: colorSecondary is deprecated.
                        return key.startsWith('color') &&
                            key !== 'colorSecondary'
                    }) // fucking HACK
                    .map(([key, val]) => this.themeToObj(key, val))

                this.mode = this.theme.mode
            },
            immediate: true
        },
        mode: 'emitValue',
        localTheme: {
            handler: 'emitValue',
            deep: true
        }
    },
    mounted () {
        const { tippyRef, picker, userPref } = this.$refs
        const pickerTheme = 'light-border'
        const tippyInstances = tippy(tippyRef, {
            content: ref => picker.$el,
        })

        const singleton = createSingleton(tippyInstances, {
            interactive: true,
            appendTo: 'parent',
            maxWidth: 300,
            //trigger: 'click', // DEBUG ONLY
            placement: 'top-start',
            theme: pickerTheme,
            // Dark theme on color picker looks bad. Stay on light theme.
            onAfterUpdate (instance, partialProps) {
                const { theme } = partialProps
                if (theme && theme !== pickerTheme) {
                    instance.setProps({ theme: pickerTheme })
                }
            }
        })
        const userPrefTippy = tippy(userPref.$el)

        this.$once('hook:destroyed', () => {
            singleton.destroy()
            userPrefTippy.destroy()
            tippyInstances.forEach(t => t.destroy())
        })
    },
    methods: {
        emitValue () {
            const themeToEmit = this.localTheme.reduce((memo, theme) => {
                return { ...memo, [theme.key]: theme.value }
            }, {})
            themeToEmit.mode = this.mode

            // Prevent infinite update loop
            const hasChanged = !_.isEqual(themeToEmit, this.theme)
            if (hasChanged) {
                this.$emit('update:theme', themeToEmit)
            }
        },
        themeToObj (key, value) {
            let displayName
            let desc
            if (key === 'color') {
                displayName = 'Primary Color'
                desc = ''
            } else if (key === 'colorSecondary') {
                displayName = 'Gradient Color'
                desc = ''
            } else if (key === 'colorContrast') {
                displayName = 'Text Color'
                desc = ''
            }
            const isPickerOpen = this.localTheme
                .find(t => t.key === key)?.isPickerOpen || false
            return { key, value, displayName, desc, isPickerOpen }
        },
        updateTheme (theme, color) {
            theme.value = color.hex
        },
        setActiveTheme (theme) {
            this.localTheme.forEach(t => (t.isPickerOpen = false))
            theme.isPickerOpen = true
        },
        closePickers (event) {
            this.localTheme.forEach(t => (t.isPickerOpen = false))
        },
    },
    components: {
        SketchPicker,
    }
}
</script>

<style lang="scss" scoped>
.color-picker {
    z-index: 10;
    // Inherit tippy.js styling over vue-color styling
    box-shadow: none;
    padding: 0;
}
.color-field {
    .input {
        max-width: 100px;
    }
}
.field.has-addons {
    display: inline-flex;
}

.theme-options {
    display: flex;
    flex-wrap: wrap;
    > .field {
        margin-right: 1.5rem;
        margin-bottom: 0;
    }
}
</style>
